import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { ExportMenu } from "../../ui/export-menu";
import { useChartColors } from "../../../hooks/useChartColors";
import ManagerialLoader from "../../../components/loaders/ManagerialLoader";
import { SummaryHeader2 } from "../../ui/SummaryHeader2";
import { useWholesaleMultiChannel } from "../hooks/useMultiChannelWholesale";
import { formatCurrency } from "../../../utils/formatCurrency";

export function WholesaleMultiChannel() {
  const [timeRange, setTimeRange] = useState<"3m" | "6m" | "12m">("6m");
  // const [metric, setMetric] = useState<"revenue" | "growth">("revenue");

  const { data, isLoading } = useWholesaleMultiChannel(getTimeRangeMonths(timeRange));
  const colors = useChartColors("wholesale");

  if (!colors) return null;

  const mergedData = mergeChannelData(data);
  const processedData = mergedData;
  // const processedData =
  //   metric === "growth" ? calculateGrowthData(mergedData) : mergedData;

  return (
    <>
      <SummaryHeader2
        title="Wholesale-Channel Performance"
      />
      <Card>
        <CardHeader>
          <Button
            variant={timeRange === "3m" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange("3m")}
          >
            3M
          </Button>
          <Button
            variant={timeRange === "6m" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange("6m")}
          >
            6M
          </Button>
          <Button
            variant={timeRange === "12m" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange("12m")}
          >
            12M
          </Button>

          {/* 
          <Button
            variant={metric === "revenue" ? "default" : "outline"}
            size="sm"
            onClick={() => setMetric("revenue")}
          >
            Revenue
          </Button>
          <Button
            variant={metric === "growth" ? "default" : "outline"}
            size="sm"
            onClick={() => setMetric("growth")}
            disabled
            className="cursor-not-allowed opacity-70"
          >
            Growth (Coming Soon)
          </Button>
          */}

          <ExportMenu
            data={processedData}
            filename="channel_performance"
            buttonVariant="outline"
            buttonSize="sm"
          />
        </CardHeader>
        {isLoading ? (
          <ManagerialLoader variant="channel-perf" />
        ) : (
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={processedData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs sm:text-sm text-muted-foreground" tickLine={false} axisLine={false} />
                  <YAxis
                    className="text-xs sm:text-sm text-muted-foreground"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(Number(value))}
                    contentStyle={{
                      backgroundColor: "var(--background)",
                      borderColor: "var(--border)",
                      borderRadius: "var(--radius)",
                      fontSize: "0.875rem",
                    }}
                  />
                  <Legend />
                  <Bar dataKey="wholesale" name="Wholesale" stackId="a" fill={colors[0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        )}
      </Card>
    </>
  );
}

function getTimeRangeMonths(range: "3m" | "6m" | "12m"): number {
  switch (range) {
    case "3m":
      return 3;
    case "6m":
      return 6;
    case "12m":
      return 12;
    default:
      return 6;
  }
}

function mergeChannelData(data: any) {
  if (!data || !Array.isArray(data.wholesale)) return [];

  const wholesaleMap = new Map(data.wholesale.map((item: any) => [item.period, item.wholesale_revenue]));

  const allPeriods = Array.from(new Set([
    ...data.wholesale.map((item: any) => item.period),
  ])).sort();

  return allPeriods.map((period) => ({
    date: period,
    wholesale: wholesaleMap.get(period) ?? 0,
  }));
}

// Commented out for now
// function calculateGrowthData(data: any[]) {
//   if (data.length <= 1) return data;

//   const result = [];

//   for (let i = 1; i < data.length; i++) {
//     const currentMonth = data[i];
//     const previousMonth = data[i - 1];

//     result.push({
//       date: currentMonth.date,
//       shopify_growth: calculateGrowth(currentMonth.shopify, previousMonth.shopify),
//       wholesale_growth: calculateGrowth(currentMonth.wholesale, previousMonth.wholesale),
//     });
//   }

//   return result;
// }

// function calculateGrowth(current: number, previous: number): number {
//   if (previous === 0) return current === 0 ? 0 : 100;
//   return ((current - previous) / previous) * 100;
// }
